// <auto-generated>
// This file is automatically generated from DSL.
// Source: dsl/entities/Supplier.yaml
// Generated: 2026-07-08T12:37:01.814Z
// </auto-generated>

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { supplierService } from '@/services/procurement/SupplierService';
import { CreateSupplier, UpdateSupplier, Supplier } from '@/types/procurement/Supplier';
import { authService } from '@/services/auth/authService';

/**
 * Query key factory for Supplier
 * Includes tenant ID to ensure different tenants have separate caches
 */
export const supplierKeys = {
  all: (tenantId?: string | null) => ['suppliers', tenantId] as const,
  lists: (tenantId?: string | null) => [...supplierKeys.all(tenantId), 'list'] as const,
  list: (tenantId?: string | null, filters?: any) => [...supplierKeys.lists(tenantId), { filters }] as const,
  details: (tenantId?: string | null) => [...supplierKeys.all(tenantId), 'detail'] as const,
  detail: (id: string, tenantId?: string | null) => [...supplierKeys.details(tenantId), id] as const,
};

/**
 * Hook to fetch all Suppliers
 */
export function useSuppliers(options?: Omit<UseQueryOptions<Supplier[], Error>, 'queryKey' | 'queryFn'>) {
  // Get current tenant ID for cache key
  const tenantId = authService.getTenantId();

  return useQuery<Supplier[], Error>({
    queryKey: supplierKeys.lists(tenantId),
    queryFn: () => supplierService.getAll(),
    ...options
  });
}

/**
 * Hook to fetch a single Supplier by ID
 */
export function useSupplier(
  id: string,
  options?: Omit<UseQueryOptions<Supplier | undefined, Error>, 'queryKey' | 'queryFn'>
) {
  const tenantId = authService.getTenantId();

  return useQuery<Supplier | undefined, Error>({
    queryKey: supplierKeys.detail(id, tenantId),
    queryFn: () => supplierService.getById(id),
    enabled: !!id && id !== 'new',
    ...options
  });
}

/**
 * Hook to create a new Supplier
 */
export function useCreateSupplier() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<Supplier, Error, CreateSupplier, { previousSuppliers?: Supplier[] }>({
    mutationFn: (data: CreateSupplier) => supplierService.create(data),
    onMutate: async (newSupplier) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: supplierKeys.lists(tenantId) });

      // Snapshot previous value
      const previousSuppliers = queryClient.getQueryData<Supplier[]>(supplierKeys.lists(tenantId));

      // Optimistically update to the new value. Spread the input first so a
      // client-supplied id (offline / idempotency key) wins over the temp one.
      queryClient.setQueryData<Supplier[]>(supplierKeys.lists(tenantId), (old = []) => [
        ...old,
        { ...newSupplier, id: (newSupplier as { id?: string }).id ?? 'temp-' + Date.now() } as Supplier
      ]);

      return { previousSuppliers };
    },
    onError: (_err, _newSupplier, context) => {
      // Rollback on error
      if (context?.previousSuppliers) {
        queryClient.setQueryData(supplierKeys.lists(tenantId), context.previousSuppliers);
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: supplierKeys.lists(tenantId) });
    }
  });
}

/**
 * Hook to update an existing Supplier
 */
export function useUpdateSupplier() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<Supplier, Error, { id: string; data: UpdateSupplier }, { previousSupplier?: Supplier; previousSuppliers?: Supplier[] }>({
    mutationFn: ({ id, data }) => supplierService.update(id, data),
    onMutate: async ({ id, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: supplierKeys.detail(id, tenantId) });
      await queryClient.cancelQueries({ queryKey: supplierKeys.lists(tenantId) });

      // Snapshot previous values
      const previousSupplier = queryClient.getQueryData<Supplier>(supplierKeys.detail(id, tenantId));
      const previousSuppliers = queryClient.getQueryData<Supplier[]>(supplierKeys.lists(tenantId));

      // Optimistically update detail
      queryClient.setQueryData<Supplier>(supplierKeys.detail(id, tenantId), (old) => ({
        ...old!,
        ...data
      }) as Supplier);

      // Optimistically update list
      queryClient.setQueryData<Supplier[]>(supplierKeys.lists(tenantId), (old = []) =>
        old.map((item) => (item.id === id ? { ...item, ...data } as Supplier : item))
      );

      return { previousSupplier, previousSuppliers };
    },
    onError: (_err, { id }, context) => {
      // Rollback on error
      if (context?.previousSupplier) {
        queryClient.setQueryData(supplierKeys.detail(id, tenantId), context.previousSupplier);
      }
      if (context?.previousSuppliers) {
        queryClient.setQueryData(supplierKeys.lists(tenantId), context.previousSuppliers);
      }
    },
    onSuccess: (_, { id }) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: supplierKeys.detail(id, tenantId) });
      queryClient.invalidateQueries({ queryKey: supplierKeys.lists(tenantId) });
    }
  });
}

/**
 * Hook to delete a Supplier
 */
export function useDeleteSupplier() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<void, Error, string, { previousSuppliers?: Supplier[] }>({
    mutationFn: (id: string) => supplierService.delete(id),
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: supplierKeys.lists(tenantId) });

      // Snapshot previous value
      const previousSuppliers = queryClient.getQueryData<Supplier[]>(supplierKeys.lists(tenantId));

      // Optimistically remove from list
      queryClient.setQueryData<Supplier[]>(supplierKeys.lists(tenantId), (old = []) =>
        old.filter((item) => item.id !== id)
      );

      return { previousSuppliers };
    },
    onError: (_err, _id, context) => {
      // Rollback on error
      if (context?.previousSuppliers) {
        queryClient.setQueryData(supplierKeys.lists(tenantId), context.previousSuppliers);
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: supplierKeys.lists(tenantId) });
    }
  });
}

/**
 * Combined hook for save (create or update)
 * Automatically determines whether to create or update based on presence of ID
 */
export function useSaveSupplier() {
  const createMutation = useCreateSupplier();
  const updateMutation = useUpdateSupplier();

  return {
    mutate: (params: { id?: string; data: CreateSupplier | UpdateSupplier }, options?: any) => {
      if (params.id && params.id !== 'new') {
        return updateMutation.mutate({ id: params.id, data: params.data as UpdateSupplier }, options);
      } else {
        return createMutation.mutate(params.data as CreateSupplier, options);
      }
    },
    isPending: createMutation.isPending || updateMutation.isPending,
    isError: createMutation.isError || updateMutation.isError,
    error: createMutation.error || updateMutation.error
  };
}
